import { Component, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { ConfirmService } from "../../../Utils/_services/confirmation-dialog.service";
import { SearchBoxComponent } from "../../../shared/components/search-box/search-box.component";
import { GenericQuestionsService } from "../../../Utils/_services/admin/GenericQuestions/generic-questions.service";
import { IGenericQuestion } from "../../../Utils/_models/generic-questions.model";

@Component({
  selector: "app-generic-questions",
  standalone: true,
  imports: [CommonModule, FormsModule, SearchBoxComponent],
  templateUrl: "./generic-questions.component.html",
  styleUrls: ["./generic-questions.component.css"],
})
export class GenericQuestionsComponent implements OnInit {
  questions = signal<IGenericQuestion[]>([]);
  searchTerm = "";
  currentPage = 1;
  pageSize = 5;
  pageSizes = [5, 10, 20, 50];
  includeDeleted = signal(false);

  currentSortField = signal<string | null>(null);
  currentSortOrder = signal<'asc' | 'desc' | null>(null);
  originalQuestions = signal<IGenericQuestion[]>([]);

  showModal = false;
  isEditMode = false;
  selectedQuestion: IGenericQuestion | null = null;
  isLoading = signal(false);

  formData = {
    question: "",
    group: "",
    duration: 30,
  };
  private badgeColors = [
    { bg: '#e0f2fe', text: '#0369a1', border: '#bae6fd' }, // Blue
    { bg: '#dcfce7', text: '#15803d', border: '#bbf7d0' }, // Green
    { bg: '#fce7f3', text: '#be185d', border: '#fbcfe8' }, // Pink
    { bg: '#fef3c7', text: '#b45309', border: '#fde68a' }, // Amber
    { bg: '#f3e8ff', text: '#7e22ce', border: '#e9d5ff' }, // Purple
    { bg: '#ecfccb', text: '#4d7c0f', border: '#d9f99d' }, // Lime
    { bg: '#cffafe', text: '#0f766e', border: '#a5f3fc' }, // Cyan
    { bg: '#fee2e2', text: '#b91c1c', border: '#fecaca' }, // Red
    { bg: '#ede9fe', text: '#5b21b6', border: '#ddd6fe' }, // Violet
    { bg: '#ffedd5', text: '#c2410c', border: '#fed7aa' }, // Orange
  ];

  groupOptions = signal<any[]>([]);
  isNewGroupMode = false;

  // Filtered questions list mapped to template
  filteredQuestions: IGenericQuestion[] = [];
  totalCount = 0;
  totalPages = 1;
  startIndex = 0;
  endIndex = 0;

  allSelected = signal(false);

  constructor(
    private toastr: ToastrService,
    private confirmationService: ConfirmService,
    private genericQuestionsService: GenericQuestionsService
  ) { }

  ngOnInit(): void {
    this.loadQuestions();
    this.loadGroupOptions();
  }

  loadGroupOptions(): void {
    this.genericQuestionsService.getGroups().subscribe({
      next: (res: any) => {
        if (res?.data) {
          this.groupOptions.set(res.data);
        }
      },
      error: (err) => {
        this.toastr.error("Failed to load group options");
      }
    });
  }

  toggleNewGroupMode(): void {
    this.isNewGroupMode = !this.isNewGroupMode;
    if (!this.isNewGroupMode) {
      if (this.groupOptions().length > 0) {
        // If it's a name, find matching group ID, else select first
        const match = this.groupOptions().find(g => g.name.toLowerCase() === this.formData.group.toLowerCase().trim());
        this.formData.group = match ? match._id : this.groupOptions()[0]._id;
      }
    } else {
      // If switching to custom mode, display group name instead of ID if a matching group exists
      const match = this.groupOptions().find(g => g._id === this.formData.group);
      this.formData.group = match ? match.name : "";
    }
  }

  loadQuestions(): void {
    this.genericQuestionsService.getAll({
      page: this.currentPage,
      limit: this.pageSize,
      search: this.searchTerm,
      includeDeleted: this.includeDeleted()
    }).subscribe({
      next: (res) => {
        // Map _id from backend to id field
        const items = res.data.items.map((item: any) => ({
          ...item,
          id: item._id || item.id,
          selected: false
        }));
        this.questions.set(items);
        this.originalQuestions.set([...items]);
        this.totalCount = res.data.total;
        this.totalPages = res.data.totalPages;
        if (this.currentSortField() && this.currentSortOrder()) {
          this.sortQuestionsLocal();
        } else {
          this.filteredQuestions = items;
          this.startIndex = this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
          this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalCount);
          this.updateSelectAllState();
        }
      },
      error: (err) => {
        this.toastr.error("Failed to load questions");
        this.questions.set([]);
        this.originalQuestions.set([]);
        this.filteredQuestions = [];
        this.totalCount = 0;
        this.totalPages = 1;
        this.startIndex = 0;
        this.endIndex = 0;
      }
    });
  }

  toggleIncludeDeleted(): void {
    this.includeDeleted.update(v => !v);
    this.currentPage = 1;
    this.loadQuestions();
  }

  onSearch(query: string): void {
    this.searchTerm = query;
    this.currentPage = 1;
    this.loadQuestions();
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.currentPage = 1;
    this.loadQuestions();
  }

  resetSearch(): void {
    this.searchTerm = "";
    this.currentPage = 1;
    this.loadQuestions();
    this.toastr.success("Questions reloaded");
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadQuestions();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadQuestions();
    }
  }

  // Multi-selection methods
  toggleSelectAll(): void {
    this.allSelected.update(v => !v);
    this.filteredQuestions.forEach(q => {
      q.selected = this.allSelected();
    });
    this.questions.update(orig => [...orig]);
  }

  updateSelectAllState(): void {
    const selectedCount = this.filteredQuestions.filter(q => q.selected).length;
    this.allSelected.set(
      selectedCount === this.filteredQuestions.length &&
      this.filteredQuestions.length > 0
    );
    this.questions.update(orig => [...orig]);
  }

  hasSelection(): boolean {
    return this.questions().some(q => q.selected);
  }

  async deleteSelectedQuestions(): Promise<void> {
    const selected = this.questions().filter(q => q.selected);
    if (selected.length === 0) return;

    const confirmed = await this.confirmationService.confirm({
      header: "Delete Questions",
      bodyHeader: `Are you sure you want to delete ${selected.length} questions?`,
      bodySubtitle: "This action cannot be undone.",
      yesButtonText: "Delete",
      noButtonText: "Cancel",
      yesBtnCss: "btn-danger",
      noBtnCss: "btn-secondary",
      width: "420px",
    });

    if (confirmed) {
      const selectedIds = selected.map(q => q.id);
      this.genericQuestionsService.bulkDeleteQuestions(selectedIds).subscribe({
        next: () => {
          this.toastr.success("Selected questions deleted successfully");
          this.loadQuestions();
        },
        error: (err) => {
          this.toastr.error("Failed to delete selected questions");
        }
      });
    }
  }

  trackById(index: number, item: IGenericQuestion): string {
    return item.id;
  }

  // Modal actions
  openCreateModal(): void {
    this.isEditMode = false;
    this.selectedQuestion = null;
    this.isNewGroupMode = this.groupOptions().length === 0;
    this.formData = { question: "", group: this.groupOptions()[0]?._id || "", duration: 30 };
    this.showModal = true;
  }

  openEditModal(q: IGenericQuestion): void {
    this.isEditMode = true;
    this.selectedQuestion = q;
    const groupVal = typeof q.group === 'object' && q.group ? q.group._id : q.group;
    const groupName = typeof q.group === 'object' && q.group ? q.group.name : q.group;

    const isNew = !this.groupOptions().map(g => g._id).includes(groupVal);
    this.isNewGroupMode = isNew;

    this.formData = {
      question: q.question,
      group: isNew ? (groupName || "") : (groupVal || ""),
      duration: q.duration || 30,
    };
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
  }

  onSubmit(): void {
    if (!this.formData.question.trim() || !this.formData.group.trim()) return;

    this.isLoading.set(true);
    if (this.isEditMode && this.selectedQuestion) {
      // Edit mode (updating existing question)
      this.genericQuestionsService.updateQuestion({
        id: this.selectedQuestion.id,
        question: this.formData.question.trim(),
        group: this.formData.group.trim(),
        duration: this.formData.duration,
      }).subscribe({
        next: () => {
          this.toastr.success("Question updated successfully");
          this.isLoading.set(false);
          this.closeModal();
          this.loadQuestions();
          this.loadGroupOptions();
        },
        error: (err) => {
          this.toastr.error("Failed to update question");
          this.isLoading.set(false);
        }
      });
    } else {
      // Create mode
      this.genericQuestionsService.createQuestion({
        question: this.formData.question.trim(),
        group: this.formData.group.trim(),
        duration: this.formData.duration,
      }).subscribe({
        next: () => {
          this.toastr.success("Question created successfully");
          this.isLoading.set(false);
          this.closeModal();
          this.loadQuestions();
          this.loadGroupOptions();
        },
        error: (err) => {
          this.toastr.error("Failed to create question");
          this.isLoading.set(false);
        }
      });
    }
  }

  async deleteQuestion(q: IGenericQuestion): Promise<void> {
    const confirmed = await this.confirmationService.confirm({
      header: "Delete Question",
      bodyHeader: "Are you sure you want to delete this question?",
      bodySubtitle: "This action cannot be undone.",
      yesButtonText: "Delete",
      noButtonText: "Cancel",
      yesBtnCss: "btn-danger",
      noBtnCss: "btn-secondary",
      width: "420px",
    });

    if (confirmed) {
      this.genericQuestionsService.bulkDeleteQuestions([q.id]).subscribe({
        next: () => {
          this.toastr.success("Question deleted successfully");
          this.loadQuestions();
        },
        error: (err) => {
          this.toastr.error("Failed to delete question");
        }
      });
    }
  }


  getGroupBadgeStyle(group: any): { [key: string]: string } {
    const groupName =
      (typeof group === 'object' && group ? group.name : group) || 'General';
    const hash = [...groupName].reduce((acc, char) => acc + char.charCodeAt(0), 0);

    const color = this.badgeColors[hash % this.badgeColors.length];

    return {
      'background-color': color.bg,
      color: color.text,
      border: `1px solid ${color.border}`,
    };
  }

  onSort(field: string): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.sortQuestionsLocal();
  }

  sortQuestionsLocal(): void {
    const field = this.currentSortField();
    const order = this.currentSortOrder();

    if (!field || !order) {
      this.filteredQuestions = [...this.originalQuestions()];
      this.startIndex = this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
      this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalCount);
      this.updateSelectAllState();
      return;
    }

    const direction = order === "asc" ? 1 : -1;
    const sorted = [...this.questions()];

    sorted.sort((a: any, b: any) => {
      let valA: any = a[field];
      let valB: any = b[field];

      if (field === 'question') {
        valA = (a.question || '').toLowerCase();
        valB = (b.question || '').toLowerCase();
      } else if (field === 'group') {
        const groupA = (a.group?.name || a.group || 'General').toLowerCase();
        const groupB = (b.group?.name || b.group || 'General').toLowerCase();
        valA = groupA;
        valB = groupB;
      }

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });

    this.filteredQuestions = sorted;
    this.startIndex = this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalCount);
    this.updateSelectAllState();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }
}
